import React, { useEffect, useState } from 'react';
import apiClient from '../../api/client';
import Modal from '../../components/Common/Modal';
import { fetchMarketplaceCategories, encodeCategories, FINOS_CATEGORIES } from '../../utils/categories';
import { getResellerSiteUrl } from '../../utils/resellerSiteUrl';
import { apiError, longDate, rs, shortDate } from '../format';
import DataGrid from '../../components/Common/DataGrid';
import Select2 from '../../components/Common/Select2';
import { Async, BucketPill, Button, StatusPill } from '../ui';
import useFetch from '../useFetch';
import { useDash } from '../DashboardShell';
import { PROOF_ACCEPT, ProofCell, proofError, uploadProof } from '../Proof';

const BUCKETS = ['Approved', 'Pending', 'In progress', 'Declined'];

/** Add a reseller by hand; optionally approve now so the set-password email goes out. */
export const AddResellerModal = ({ open, onClose, onDone }) => {
  const { notify } = useDash();
  const empty = { name: '', business_name: '', subdomain: '', email: '', phone: '', approve: true };
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [options, setOptions] = useState(FINOS_CATEGORIES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    setCats([]);
    setError('');
    fetchMarketplaceCategories(apiClient).then(setOptions).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggle = (id) => setCats((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/admin/resellers', {
        ...form,
        subdomain: form.subdomain.trim().toLowerCase(),
        business_name: form.business_name || null,
        phone: form.phone || null,
        market_focus: encodeCategories(cats, options.map((o) => o.id)) || null,
      });
      notify(form.approve ? `${form.name} added. Set-password email sent.` : `${form.name} added as pending.`);
      onDone();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Could not add the reseller'));
      if (err?.response?.status === 502) onDone(); // reseller exists, only the email failed
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={open} title="Add reseller" onClose={onClose}>
      <form className="ds-form" onSubmit={submit}>
        {error && <div className="ds-formerror" role="alert">{error}</div>}
        <div className="ds-formgrid">
          <div className="ds-field"><label htmlFor="ar-name">Full name</label><input id="ar-name" className="ds-input" required value={form.name} onChange={set('name')} /></div>
          <div className="ds-field"><label htmlFor="ar-biz">Business name</label><input id="ar-biz" className="ds-input" value={form.business_name} onChange={set('business_name')} /></div>
          <div className="ds-field">
            <label htmlFor="ar-sub">Subdomain</label>
            <input id="ar-sub" className="ds-input" required minLength={3} maxLength={30} pattern="[a-z0-9\-]+" value={form.subdomain} onChange={set('subdomain')} />
            <small>{form.subdomain ? getResellerSiteUrl(form.subdomain.toLowerCase(), { withProtocol: false }) : 'Lowercase letters, numbers and hyphens'}</small>
          </div>
          <div className="ds-field"><label htmlFor="ar-email">Email</label><input id="ar-email" type="email" className="ds-input" required value={form.email} onChange={set('email')} /></div>
          <div className="ds-field"><label htmlFor="ar-phone">Phone</label><input id="ar-phone" className="ds-input" value={form.phone} onChange={set('phone')} /></div>
        </div>
        <fieldset className="ds-field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend>Categories <small>(none selected means all)</small></legend>
          <div className="ds-checkgrid">
            {options.map((c) => (
              <label key={c.id} className="ds-check">
                <input type="checkbox" checked={cats.includes(c.id)} onChange={() => toggle(c.id)} />
                {c.label}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="ds-check">
          <input type="checkbox" checked={form.approve} onChange={set('approve')} />
          Approve now and email the set-password link
        </label>
        <div className="ds-modal-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add reseller'}</Button>
        </div>
      </form>
    </Modal>
  );
};

/** Record money already paid to a reseller. `reseller` may be null: then pick one with a balance. */
export const PayoutModal = ({ open, reseller, choices = [], onClose, onDone }) => {
  const { notify } = useDash();
  const [pickedId, setPickedId] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const target = reseller || choices.find((c) => String(c.id) === String(pickedId)) || null;
  const balance = target ? target.commission_balance : 0;

  useEffect(() => {
    if (!open) return;
    setError('');
    setReference('');
    setNote('');
    setProof(null);
    setPickedId('');
    setAmount(reseller ? String(reseller.commission_balance) : '');
  }, [open, reseller]);

  const pick = (id) => {
    setPickedId(id);
    const c = choices.find((x) => String(x.id) === String(id));
    setAmount(c ? String(c.commission_balance) : '');
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = Number(amount);
    if (!target) return setError('Choose a reseller');
    if (!(value > 0)) return setError('Enter an amount above zero');
    if (Math.round(value * 100) > Math.round(balance * 100)) return setError(`That is more than the balance owed (${rs(balance)})`);
    const fileProblem = proofError(proof);
    if (fileProblem) return setError(fileProblem);
    setBusy(true);
    setError('');
    try {
      const { data: payout } = await apiClient.post(`/admin/resellers/${target.id}/payout`, {
        amount: value,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      let message = `Recorded ${rs(value)} paid to ${target.name}`;
      let type = 'success';
      if (proof) {
        try {
          await uploadProof(payout.id, proof);
          message += ' with proof';
        } catch (err) {
          // The payout itself is saved; only the receipt failed. Say so and let them add it from Payouts.
          message += `. The proof was not saved: ${apiError(err, 'upload failed')}. Add it from the Payouts page.`;
          type = 'error';
        }
      }
      notify(message, type);
      onDone();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Could not record the payout'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={open} title="Record payout" onClose={onClose}>
      <form className="ds-form" onSubmit={submit}>
        <p className="ds-confirm-text">Record a payment you have already made. This does not move any money.</p>
        {error && <div className="ds-formerror" role="alert">{error}</div>}
        {reseller ? (
          <p><strong>{reseller.name}</strong> · balance owed <strong>{rs(balance)}</strong></p>
        ) : (
          <div className="ds-field">
            <label htmlFor="po-who">Reseller</label>
            <Select2
              id="po-who"
              value={pickedId}
              onChange={pick}
              placeholder="Choose a reseller"
              options={choices.map((c) => ({ value: String(c.id), label: `${c.name} · owed ${rs(c.commission_balance)}` }))}
            />
          </div>
        )}
        <div className="ds-formgrid">
          <div className="ds-field">
            <label htmlFor="po-amount">Amount paid (Rs)</label>
            <input id="po-amount" className="ds-input" type="number" min="1" step="0.01" max={balance || undefined} required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="ds-field">
            <label htmlFor="po-ref">Bank reference</label>
            <input id="po-ref" className="ds-input" maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TXN-12345" />
          </div>
        </div>
        <div className="ds-field">
          <label htmlFor="po-note">Note</label>
          <input id="po-note" className="ds-input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="ds-field">
          <label htmlFor="po-proof">Proof of payment</label>
          <input id="po-proof" className="ds-input" type="file" accept={PROOF_ACCEPT} onChange={(e) => setProof(e.target.files?.[0] || null)} style={{ padding: '9px 14px', height: 'auto' }} />
          <small className="ds-filehint">Optional. A bank slip or screenshot (PDF, JPG, PNG or WebP, up to 5 MB). The reseller can see it.</small>
        </div>
        <div className="ds-modal-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record payout'}</Button>
        </div>
      </form>
    </Modal>
  );
};

/** Everything about one reseller: figures, bank details, rate, payouts and account actions. */
export const ResellerDetailModal = ({ resellerId, onClose, onChanged, onPay }) => {
  const { notify, confirm } = useDash();
  const state = useFetch(resellerId ? `/admin/resellers/${resellerId}` : null);
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.data) setRate(String(Math.round(state.data.commission_rate * 1000) / 10));
  }, [state.data]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      notify(okMessage);
      onChanged();
      state.reload();
    } catch (err) {
      notify(apiError(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveRate = (e) => {
    e.preventDefault();
    const pct = Number(rate);
    if (!(pct >= 0 && pct <= 50)) return notify('Rate must be between 0% and 50%', 'error');
    act(() => apiClient.put(`/admin/resellers/${resellerId}/commission-rate`, { rate: pct / 100 }), `Rate set to ${pct}%. It applies to future approvals.`);
  };

  const removeReseller = async (r) => {
    const ok = await confirm({
      title: `Delete ${r.name}?`,
      message: 'This permanently removes the reseller, their login, customers, commissions and payouts. It cannot be undone.',
      confirmLabel: 'Delete reseller',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.delete(`/resellers/${r.id}`);
      notify(`${r.name} deleted`);
      onChanged();
      onClose();
    } catch (err) {
      notify(apiError(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (r) => {
    const ok = await confirm({
      title: `Deactivate ${r.name}?`,
      message: 'Their partner site stops working, they cannot sign in, and they earn no new commission. Commission already earned and any payouts stay as they are. You can turn them back on any time.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (ok) act(() => apiClient.post(`/admin/resellers/${r.id}/deactivate`), `${r.name} is now inactive`);
  };

  return (
    <Modal isOpen={!!resellerId} title="Reseller" onClose={onClose}>
      <Async state={state}>
        {(r) => (
          <div className="ds-form">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <strong style={{ fontSize: 18 }}>{r.name}</strong>
              <StatusPill status={r.status} />
              <span style={{ color: '#5a6a7e', fontSize: 13 }}>{r.business_name || 'Independent'}</span>
            </div>
            {r.status === 'suspended' && (
              <p className="ds-callout">Inactive: the partner site is switched off, sign-in is blocked and no new commission is credited.</p>
            )}
            <dl className="ds-dl">
              <dt>Site</dt><dd>{getResellerSiteUrl(r.subdomain, { withProtocol: false })}</dd>
              <dt>Email</dt><dd>{r.email}</dd>
              <dt>Phone</dt><dd>{r.phone || '–'}</dd>
              <dt>Categories</dt><dd>{r.market_focus || 'all'}</dd>
              <dt>Joined</dt><dd>{longDate(r.created_at)}</dd>
              <dt>Conversions</dt><dd>{r.conversions}</dd>
              <dt>Earned</dt><dd>{rs(r.commission)}</dd>
              <dt>Paid</dt><dd>{rs(r.commission_paid)}</dd>
              <dt>Balance owed</dt><dd><strong>{rs(r.commission_balance)}</strong></dd>
              <dt>Bank</dt>
              <dd>
                {r.bank.bank_name ? `${r.bank.bank_name} · ${r.bank.account_title || ''}` : <span style={{ color: '#9b2418' }}>Not added yet</span>}
                {r.bank.account_number && <small style={{ display: 'block', color: '#5a6a7e' }}>{r.bank.account_number}</small>}
              </dd>
            </dl>

            <div className="ds-pills">
              {BUCKETS.map((b) => <BucketPill key={b} bucket={b} label={`${b} ${r.customer_status[b] || 0}`} />)}
            </div>

            <form className="ds-field" onSubmit={saveRate}>
              <label htmlFor="rd-rate">Commission rate (%)</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input id="rd-rate" className="ds-input" type="number" min="0" max="50" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} style={{ maxWidth: 140 }} />
                <Button type="submit" disabled={busy}>Save rate</Button>
              </div>
              <small>Applies to applications approved from now on. Commission already earned stays as it is.</small>
            </form>

            {r.payouts.length > 0 && (
              <div>
                <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Recent payouts</h3>
                <DataGrid
                  simple
                  rows={r.payouts}
                  order={[[0, 'desc']]}
                  columns={[
                    { title: 'Date', render: (p) => shortDate(p.paid_at), value: (p) => p.paid_at || '' },
                    { title: 'Reference', render: (p) => p.reference || '–', value: (p) => p.reference || '' },
                    { title: 'Proof', render: (p) => <ProofCell payout={p} base="/admin" canUpload onChanged={state.reload} /> },
                    { title: 'Amount', numeric: true, render: (p) => rs(p.amount), value: (p) => p.amount },
                  ]}
                />
              </div>
            )}

            <div className="ds-modal-actions" style={{ justifyContent: 'flex-start' }}>
              <Button variant="primary" disabled={busy || r.commission_balance <= 0} onClick={() => onPay(r)}>Record payout</Button>
              {r.status === 'pending' && (
                <Button variant="primary" disabled={busy} onClick={() => act(() => apiClient.post(`/resellers/${r.id}/approve`), 'Approved. Set-password email sent.')}>Approve</Button>
              )}
              {r.status === 'suspended' && (
                <Button variant="primary" disabled={busy} onClick={() => act(() => apiClient.post(`/admin/resellers/${r.id}/activate`), `${r.name} is active again`)}>Activate</Button>
              )}
              {r.status === 'active' && (
                <>
                  <Button disabled={busy} onClick={() => act(() => apiClient.post(`/resellers/${r.id}/resend-invite`), `Invite re-sent to ${r.email}`)}>Resend invite</Button>
                  <Button disabled={busy} onClick={() => deactivate(r)}>Deactivate</Button>
                </>
              )}
              <Button variant="danger" disabled={busy} onClick={() => removeReseller(r)}>Delete</Button>
            </div>
          </div>
        )}
      </Async>
    </Modal>
  );
};
